import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate } from '@angular/service-worker';
import { NotificationService } from './notification_service';
import { Util } from '../util/util';
import { CacheService } from './cache_service';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

@Injectable({
  providedIn: 'root'
})

export class A2hsService {
  promptIntercepted = false;
  isStandalone = false;
  deferredPrompt: BeforeInstallPromptEvent | null = null; 
  userInstalled = false;

  // user agent
  isChrome = false;
  isExplorer = false;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  isExplorer_11 = false;
  isFirefox = false;
  isSafari = false;
  isOpera = false;
  isEdgeDesktop = false;
  isEdgeiOS = false;
  isEdgeAndroid = false;
  userAgent = '';

  isIOS = false;
  isMobile = false;
  hideBeforeInstallPrompt = false;

  promptSaved = false;
  deferredPromptShown = false;
  deferredPromptRejected = false;

  private swUpdate = inject(SwUpdate);
  private notificationService = inject(NotificationService);
  private cacheService = inject(CacheService);
  private util = inject(Util);
  private platformId = inject(PLATFORM_ID); 

  constructor() {
    this.swUpdate.versionUpdates.subscribe(() => {
        this.notificationService.notify({
            message: 'There is new update available for Notd',
            type: 'success',
            button: {
            label: 'Update',
            action: 'makePwaAppUpdate'
            }
        });
    });

    this.hideBeforeInstallPrompt = this.cacheService.cache.get('dismissedA2HS') || false;

    if (isPlatformBrowser(this.platformId)) {
        this.setupPwaListeners();
    }
  }

  private setupPwaListeners() {
      window.addEventListener('beforeinstallprompt', (e) => {
          console.log('Got beforeinstallprompt event');
          e.preventDefault();
          this.deferredPrompt = e as BeforeInstallPromptEvent;
          this.promptIntercepted = true;
      });

      window.addEventListener('appinstalled', () => {
          this.trackInstalled();
      });
      
      this.checkUserAgent();
      this.trackStandalone();
  }

  checkUserAgent() {
    if (!isPlatformBrowser(this.platformId)) {
        return;
    }
    
    this.userAgent = navigator.userAgent.toLowerCase();
    const uaString = this.userAgent;

    this.isChrome = /chrome/.test(uaString);
    this.isExplorer = /msie/.test(uaString);
    this.isExplorer_11 = /rv:11/.test(uaString);
    this.isFirefox  = /firefox/.test(uaString);
    this.isSafari = /safari/.test(uaString);
    this.isOpera = /opr/.test(uaString);
    this.isEdgeDesktop = /edge/.test(uaString);
    this.isEdgeiOS = /edgios/.test(uaString);
    this.isEdgeAndroid = /edga/.test(uaString);

    this.isIOS = this.isIosDevice(); 
    this.isMobile = this.util.isTouchDevice(); 
    
    if ((this.isChrome) && (this.isSafari)) {
      this.isSafari = false;
    }
    if ((this.isChrome) && (  (this.isEdgeDesktop) ||
                              (this.isEdgeiOS) ||
                              (this.isEdgeAndroid) )  ) {
                                this.isChrome = false;
                              }
    if ((this.isSafari) && (  (this.isEdgeDesktop) ||
                              (this.isEdgeiOS) ||
                              (this.isEdgeAndroid) )  ) {
                                this.isSafari = false;
                              }
    if ((this.isChrome) && (this.isOpera)) {
      this.isChrome = false;
    }
  }


  trackStandalone() {
    this.isStandalone = this.isInStandaloneMode();
  }

  trackInstalled() {
    this.userInstalled = true;
  }

  addToHomeScreen() {
    if (!isPlatformBrowser(this.platformId)) {
        return;
    }

    const promptEvent = this.deferredPrompt as BeforeInstallPromptEvent | null;
    
    if (!promptEvent) {
      return;
    }

    promptEvent.prompt();
    this.deferredPromptShown = true;

    promptEvent.userChoice
      .then(choiceResult => {
        if (choiceResult.outcome === 'accepted') {
            console.log('User accepted the A2HS prompt');
            this.trackInstalled();
        } else {
            this.cacheService.setCache('dismissedA2HS', true);
            this.deferredPromptRejected = true;
        }
      });

    this.deferredPrompt = null;
  }

  public showAddToHomeScreen(): boolean {
    return isPlatformBrowser(this.platformId) && this.isMobile && this.promptIntercepted && !this.userInstalled && !this.hideBeforeInstallPrompt;
  }

  public showIosInstall(): boolean {
    return isPlatformBrowser(this.platformId) && this.isSafari && this.isIOS && !this.isStandalone && !this.hideBeforeInstallPrompt;
  }

  public closeA2hsPopup() {
    this.cacheService.setCache('dismissedA2HS', true);
    this.hideBeforeInstallPrompt = true;
  }

  private isIosDevice(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
        return false;
    }
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test( userAgent );
  }

  private isInStandaloneMode(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
        return false;
    }

    interface StandaloneNavigator extends Navigator {
      standalone?: boolean;
    }
    
    const nav = window.navigator as StandaloneNavigator;
    return ('standalone' in nav) && (nav.standalone === true);
  }
}